import { ChangeDetectorRef, Component, Input, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { LaneManagerService } from './shared/services/lane-manager/lane-manager.service';
import { WorkItemTypes } from 'common/workitems/constants/workitem-types';
import { AsyncMessageLayout, ChatLayout, EmailLayout, TextChatLayout } from './layouts';
import { TranslationLoaderService } from 'shared/services/translation-loader/translation-loader.service';
import { IntegrationWorkItemLaneModel, WorkItemLaneModel } from './shared/services/lane-manager/work-item-lane.model';
import { WorkItemLaneType } from './shared/services/lane-manager/work-item-lane-type.enum';
import { BcSettingsService } from 'common/settings/settings.service';
import { Settings } from 'common/settings/settings.interface';
import { BcFeatures } from 'common/common/constants/feature-enums';
import { BcAnalytics } from 'common/analytics/analytics.service';
import { laneButtonsEvents } from 'common/common/constants/analytics-enums';
import { DiscussionsService } from '../discussions/discussions.service';
import { NanorepService } from '../nanorep-panel/nanorep.service';
import { CoolLocalStorage } from '@angular-cool/storage';
import { isEqual, sortBy } from 'lodash';
import { RxEmitter } from 'common/rxjs/rx-emitter';
import { Subscription } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { MessageBusEvents } from 'common/common/constants/message-bus-events';
import { bcSessionKeys } from 'common/common/constants/session-keys';

@Component({
  selector: 'lane-buttons',
  templateUrl: './lane-buttons.component.html',
  styleUrls: ['./lane-buttons.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class LaneButtonsComponent implements OnInit, OnDestroy {
  @Input()
  workItemType: string;

  @Input()
  workItemId: string;

  @Input()
  isWorkItemEnded: boolean;

  @Input()
  isNanorepIntegratedChatWorkItem: boolean = false;

  private _laneObservable;
  private _isDiscussionsEnabled: boolean = false;
  private _isNanorepEnabled: boolean = false;
  private _saveDefaultSettingsSubscription: Subscription;

  public workItemLanes: WorkItemLaneModel[];
  public isSettingDifferent: boolean = false;
  public itemType: string;

  constructor(private laneManager: LaneManagerService,
              private translationLoader: TranslationLoaderService,
              private nanorepService: NanorepService,
              private _settingsService: BcSettingsService,
              private _bcAnalytics: BcAnalytics,
              private _discussionsService: DiscussionsService,
              private _localStorage: CoolLocalStorage,
              private _rxEmitter: RxEmitter,
              private _translateService: TranslateService,
              private _changeDetector: ChangeDetectorRef) {
  }

  async ngOnInit(): Promise<void> {
    this.translationLoader.loadTranslations();

    this._subscribeToLaneObservable();
    this._subscribeToDefaultSettings();
    let laneLayout: WorkItemLaneModel[];
    this._isDiscussionsEnabled = await this._hasDiscussionsFeature();

    switch (this.workItemType) {
      case WorkItemTypes.CHAT:
        this._isNanorepEnabled = await this._hasNanorepFeature();
        const isAiChat = this._isNanorepEnabled && this.isNanorepIntegratedChatWorkItem;

        laneLayout = this._initLayoutForWorkItem(ChatLayout, this._isDiscussionsEnabled, isAiChat);

        this.itemType = isAiChat ?
          this._translateService.instant('laneButtons.aiEnabledChats') :
          this._translateService.instant('laneButtons.chats');
        break;
      case WorkItemTypes.SMS:
      case WorkItemTypes.FACEBOOK:
        this.itemType = this._translateService.instant('laneButtons.messages');
        laneLayout = TextChatLayout;
        break;
      case WorkItemTypes.ASYNC_MESSAGE:
        this._isNanorepEnabled = await this._hasNanorepFeature();
        this.itemType = this._translateService.instant('laneButtons.messages');
        laneLayout = this._initLayoutForWorkItem(AsyncMessageLayout, false, this._isNanorepEnabled);
        break;
      case WorkItemTypes.EMAIL:
        this.itemType = this._translateService.instant('laneButtons.emails');
        laneLayout = this._initLayoutForWorkItem(EmailLayout, this._isDiscussionsEnabled, false);
        break;
      default:
        throw new Error('Unknown WorkItemType for LaneButtons component.');
    }
    const storedDefaultSettings = this._getStoredDefaultSettings();
    this._initLanes(laneLayout, storedDefaultSettings);
  }

  ngOnDestroy(): void {
    if (this._laneObservable) {
      this._laneObservable.unsubscribe();
    }

    if (this._saveDefaultSettingsSubscription) {
      this._saveDefaultSettingsSubscription.unsubscribe();
    }
  }

  _initLayoutForWorkItem(defaultLayout, hasDiscussions, isAiChat) {
    let laneLayout = defaultLayout;
    if (!hasDiscussions) {
      laneLayout = laneLayout.filter(layout => layout.id !== WorkItemLaneType.Discussions.toString());
    }

    if (!isAiChat) {
      laneLayout = laneLayout.filter(layout => layout.id !== WorkItemLaneType.SmartAdvisor.toString());
    }
    return laneLayout;
  }

  onButtonClick(lane: WorkItemLaneModel): void {
    this._trackLaneButtonClick(lane);
    this.laneManager.toggleLane(this.workItemId, lane.id, this.workItemType);
  }

  isVisible(lane: WorkItemLaneModel): boolean {
    if (lane.type === WorkItemLaneType.PopupIntegration) {
      const integrationLane = <IntegrationWorkItemLaneModel>lane;
      return !!integrationLane.openedWindowRef && !integrationLane.openedWindowRef.closed;
    }

    return lane.visible;
  }

  isPopupIntegration(lane: WorkItemLaneModel): boolean {
    return lane.type === WorkItemLaneType.PopupIntegration;
  }

  public saveNewDefaultSettings(): void {
    this._saveDefaultSettingsToStorage(this.workItemLanes);
    this.isSettingDifferent = false;
    this._sendAnalytics();
  }

  private _sendAnalytics() {
    const defaultLanes = this.workItemLanes.filter(lane => lane.type !== WorkItemLaneType.PopupIntegration && lane.type !== WorkItemLaneType.EmbeddedIntegration && lane.type !== WorkItemLaneType.SalesforceIntegration);
    const integrationLanes = this.workItemLanes.filter(lane => lane.type === WorkItemLaneType.EmbeddedIntegration || lane.type === WorkItemLaneType.SalesforceIntegration);
    const isAiChatWindow = this._isNanorepEnabled && this.isNanorepIntegratedChatWorkItem;
    this._bcAnalytics.eventTrack(laneButtonsEvents.SAVE_NEW_DEFAULT_LANE_VIEW, { workItemType: this.workItemType, defaultLanes, integrationLaneCount: integrationLanes.length, isAiChatWindow });
  }

  private _subscribeToLaneObservable(): void {
    this._laneObservable = this.laneManager.getButtonObservable(this.workItemId)
      .subscribe(lanes => {
        this.workItemLanes = lanes;
        let storedDefaultSettings = this._getStoredDefaultSettings();
        if (!this.isWorkItemEnded && lanes) {
          this._compareLanesWithSavedDefaultSettings(lanes, storedDefaultSettings);
        }
        this._changeDetector.detectChanges();
      });
  }

  private _getStoredDefaultSettings(): WorkItemLaneModel[] {
    let storedDefaultSettings;
    switch (this.workItemType) {
      case WorkItemTypes.CHAT:
        if (this._isNanorepEnabled && this.isNanorepIntegratedChatWorkItem) {
          storedDefaultSettings = this._localStorage.tryGetObject(bcSessionKeys.storage.defaultSwimlanes + bcSessionKeys.storage.aiChatPostFix);
        } else {
          storedDefaultSettings = this._localStorage.tryGetObject(bcSessionKeys.storage.defaultSwimlanes + bcSessionKeys.storage.chatPostFix);
        }
        break;
      case WorkItemTypes.SMS:
      case WorkItemTypes.FACEBOOK:
      case WorkItemTypes.ASYNC_MESSAGE:
        storedDefaultSettings = this._localStorage.tryGetObject(bcSessionKeys.storage.defaultSwimlanes + bcSessionKeys.storage.textChatPostFix);
        break;
      case WorkItemTypes.EMAIL:
        storedDefaultSettings = this._localStorage.tryGetObject(bcSessionKeys.storage.defaultSwimlanes + bcSessionKeys.storage.emailPostFix);
        break;
      default:
        throw new Error('Unsupported workItem type for lane toggle default settings');
    }
    return storedDefaultSettings;
  }

  private _subscribeToDefaultSettings(): void {
    this._saveDefaultSettingsSubscription = this._rxEmitter.subscribe(`${MessageBusEvents.SaveDefaultLaneViewToStorage}${this.workItemId}`, (mergedLanes) => {
      this._saveDefaultSettings(mergedLanes);
    });
  }

  private _initLanes(layout: WorkItemLaneModel[], storedDefaultSettings): void {
    return this.laneManager.initLanes(this.workItemId, layout, this.workItemType, storedDefaultSettings);
  }

  private _compareLanesWithSavedDefaultSettings(lanes: WorkItemLaneModel[], defaultSettings: WorkItemLaneModel[]): void {
    const filteredLanes = this._filterPopUpIntegrations(lanes || []);
    let newLanesArray = this._createUnifiedLanes(filteredLanes);
    let newDefaultSettingsArray = this._createUnifiedLanes(defaultSettings || []);
    this.isSettingDifferent = !isEqual(newDefaultSettingsArray, newLanesArray);
  }

  private _filterPopUpIntegrations(lanesList) {
    return lanesList.filter(lane => lane.type !== WorkItemLaneType.PopupIntegration);
  }

  private async _hasDiscussionsFeature(): Promise<boolean> {
    const settings: Settings = await this._settingsService.getSettings();
    return this._settingsService.ChatFeatures.isEnabled(settings, BcFeatures.LC_DISCUSSIONS) && this._isDiscussionsFeatureEnabled() && this._hasDiscussionPermission();
  }

  private _trackLaneButtonClick(lane: WorkItemLaneModel): void {
    let eventName = laneButtonsEvents.CUSTOM_PRESSED;
    switch (lane.type) {
      case WorkItemLaneType.CannedMessage:
        eventName = laneButtonsEvents.CANNED_MESSAGES_PRESSED;
        break;
      case WorkItemLaneType.VisitorInfo:
        eventName = laneButtonsEvents.VISITOR_INFO_PRESSED;
        break;
      case WorkItemLaneType.Discussions:
        eventName = laneButtonsEvents.DISCUSSIONS_PRESSED;
        break;
      case WorkItemLaneType.VisitorHistory:
        eventName = laneButtonsEvents.VISITOR_HISTORY_PRESSED;
        break;
    }
    this._bcAnalytics.eventTrack(eventName, {
      to: lane.visible ? 'hidden' : 'visible'
    });
  }

  private _isDiscussionsFeatureEnabled(): boolean {
    return this._discussionsService.isDiscussionsEnabled();
  }

  private _hasDiscussionPermission(): boolean {
    return this._discussionsService.hasDiscussionPermission(this.workItemType);
  }

  private async _hasNanorepFeature(): Promise<boolean> {
    return await this.nanorepService.hasNanorepFeature();
  }

  private _saveDefaultSettings(mergedLanes): void {
    if (!this.isWorkItemEnded && mergedLanes) {
      this._saveDefaultSettingsToStorage(mergedLanes);
    }
  }

  private _createUnifiedLanes(lanes: WorkItemLaneModel[]): Object[] {
    const filteredLanes = this._filterPopUpIntegrations(lanes);
    let newLanesArray = filteredLanes.map((lane) => {
      let { id, visible } = lane;
      return { id, visible };
    });
    return sortBy(newLanesArray, 'id');
  }

  private _saveDefaultSettingsToStorage(settings: WorkItemLaneModel[]): void {
    let newDefaultSettings = this._createUnifiedLanes(settings);

    switch (this.workItemType) {
      case WorkItemTypes.CHAT:
        if (this._isNanorepEnabled && this.isNanorepIntegratedChatWorkItem) {
          this._localStorage.setObject(bcSessionKeys.storage.defaultSwimlanes + bcSessionKeys.storage.aiChatPostFix, newDefaultSettings);
        } else {
          this._localStorage.setObject(bcSessionKeys.storage.defaultSwimlanes + bcSessionKeys.storage.chatPostFix, newDefaultSettings);
        }
        break;
      case WorkItemTypes.SMS:
      case WorkItemTypes.FACEBOOK:
      case WorkItemTypes.ASYNC_MESSAGE:
        this._localStorage.setObject(bcSessionKeys.storage.defaultSwimlanes + bcSessionKeys.storage.textChatPostFix, newDefaultSettings);
        break;
      case WorkItemTypes.EMAIL:
        this._localStorage.setObject(bcSessionKeys.storage.defaultSwimlanes + bcSessionKeys.storage.emailPostFix, newDefaultSettings);
        break;
      default:
        throw new Error('Unsupported workItemType for saving default lane toggle settings');
    }
  }
}
